import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Users, TrendingUp, PoundSterling, Briefcase, ChevronRight,
  Search, X, ArrowLeft, ArrowUpDown, ArrowUp, ArrowDown,
  Building2, Trophy, Clock, CheckCircle
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
};

const formatDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; }
};

const fmtStatus = (s) => s ? s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '—';

const CASE_STATUS_COLORS = {
  new_lead: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
  fact_find_complete: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200',
  application_submitted: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-200',
  valuation_booked: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-200',
  offer_issued: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-200',
  completed: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200',
  lost_case: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200',
};

const CHART_COLORS = ['#e63946', '#457b9d', '#2a9d8f', '#e9c46a', '#f4a261', '#264653', '#8338ec', '#3a86ff'];

export default function Referrals() {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('total_commission');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partnerCases, setPartnerCases] = useState([]);
  const [loadingCases, setLoadingCases] = useState(false);

  const fetchPartners = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/referral-partners`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to load referral partners');
      const data = await res.json();
      setPartners(data.partners || []);
      setSummary(data.summary || null);
    } catch (e) {
      toast.error('Failed to load referral partners');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPartnerCases = useCallback(async (partnerName) => {
    try {
      setLoadingCases(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/referral-partners/${encodeURIComponent(partnerName)}/cases`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to load partner cases');
      const data = await res.json();
      setPartnerCases(data.cases || []);
    } catch (e) {
      toast.error('Failed to load partner cases');
    } finally {
      setLoadingCases(false);
    }
  }, []);

  useEffect(() => { fetchPartners(); }, [fetchPartners]);

  const handleSelectPartner = (partner) => {
    setSelectedPartner(partner);
    fetchPartnerCases(partner.name);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
    return sortDir === 'asc' ? <ArrowUp className="h-3 w-3 ml-1 text-red-600" /> : <ArrowDown className="h-3 w-3 ml-1 text-red-600" />;
  };

  const filtered = partners
    .filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const av = a[sortField] ?? 0;
      const bv = b[sortField] ?? 0;
      return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });

  const top5Chart = [...partners]
    .sort((a, b) => b.total_commission - a.total_commission)
    .slice(0, 8)
    .map(p => ({ name: p.name.length > 14 ? p.name.slice(0, 14) + '…' : p.name, commission: p.total_commission, cases: p.total_cases }));

  // ── Partner detail view ────────────────────────────────────────────────────
  if (selectedPartner) {
    return (
      <div className="p-6 space-y-6 animate-fadeIn">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => { setSelectedPartner(null); setPartnerCases([]); }}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              {selectedPartner.name}
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">Referral partner overview</p>
          </div>
        </div>

        {/* Partner stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Total Cases</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{selectedPartner.total_cases}</p>
              <p className="text-xs text-slate-500 mt-1">{selectedPartner.unique_clients} clients</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Commission Earned</p>
              <p className="text-2xl font-bold text-green-700 dark:text-green-400">{formatCurrency(selectedPartner.total_commission)}</p>
              <p className="text-xs text-slate-500 mt-1">{formatCurrency(selectedPartner.pending_commission)} pending</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Conversion Rate</p>
              <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">{selectedPartner.conversion_rate}%</p>
              <p className="text-xs text-slate-500 mt-1">{selectedPartner.completed_cases} completed</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Total Loan Value</p>
              <p className="text-2xl font-bold text-purple-700 dark:text-purple-400">{formatCurrency(selectedPartner.total_loan_value)}</p>
              <p className="text-xs text-slate-500 mt-1">{selectedPartner.mortgage_cases} mortgages · {selectedPartner.insurance_cases} insurance</p>
            </CardContent>
          </Card>
        </div>

        {/* Cases table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">All Cases from {selectedPartner.name}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loadingCases ? (
              <div className="p-8 text-center text-slate-500">Loading cases…</div>
            ) : partnerCases.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No cases found</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Case</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>Lender</TableHead>
                      <TableHead>Loan Amount</TableHead>
                      <TableHead>Commission</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {partnerCases.map(c => (
                      <TableRow key={c.case_id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                        <TableCell className="font-mono text-xs">{c.case_id}</TableCell>
                        <TableCell className="font-medium">{c.client_name}</TableCell>
                        <TableCell className="capitalize">{fmtStatus(c.product_type)}</TableCell>
                        <TableCell>{c.lender_name || '—'}</TableCell>
                        <TableCell>{formatCurrency(c.loan_amount)}</TableCell>
                        <TableCell>{formatCurrency(c.gross_commission)}</TableCell>
                        <TableCell>
                          <Badge className={`text-xs ${CASE_STATUS_COLORS[c.status] || ''}`}>
                            {fmtStatus(c.status)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-500 text-xs">{formatDate(c.date_application_submitted)}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/cases/${c.case_id}`)}>
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Main leaderboard view ──────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="referrals-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Referral Partners
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Track performance and commission from your referral network</p>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Partners</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{summary.total_partners}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Total Clients</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{summary.total_clients}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Total Cases</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{summary.total_cases}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Commission Earned</p>
              <p className="text-2xl font-bold text-green-700 dark:text-green-400">{formatCurrency(summary.total_commission)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Pending Commission</p>
              <p className="text-2xl font-bold text-yellow-700 dark:text-yellow-400">{formatCurrency(summary.pending_commission)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Total Loan Value</p>
              <p className="text-2xl font-bold text-purple-700 dark:text-purple-400">{formatCurrency(summary.total_loan_value)}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Chart + leaderboard top row */}
      {top5Chart.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Commission chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <PoundSterling className="h-4 w-4 text-red-600" /> Commission by Partner
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div style={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={top5Chart} layout="vertical" margin={{ left: 8, right: 24, top: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.2} horizontal={false} />
                    <XAxis type="number" tickFormatter={v => `£${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: '#94a3b8' }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#94a3b8' }} width={80} />
                    <Tooltip formatter={(v) => [formatCurrency(v), 'Commission']} contentStyle={{ background: '#1e293b', border: 'none', borderRadius: 8, color: '#f1f5f9' }} />
                    <Bar dataKey="commission" radius={[0, 4, 4, 0]}>
                      {top5Chart.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Cases chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-red-600" /> Cases by Partner
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div style={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={top5Chart} layout="vertical" margin={{ left: 8, right: 24, top: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.2} horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#94a3b8' }} width={80} />
                    <Tooltip formatter={(v) => [v, 'Cases']} contentStyle={{ background: '#1e293b', border: 'none', borderRadius: 8, color: '#f1f5f9' }} />
                    <Bar dataKey="cases" radius={[0, 4, 4, 0]}>
                      {top5Chart.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Leaderboard table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <CardTitle className="text-base flex items-center gap-2">
              <Trophy className="h-4 w-4 text-red-600" /> Partner Leaderboard
            </CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search partners…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 h-9 text-sm"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                  <X className="h-3 w-3 text-slate-400" />
                </button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500 dark:text-slate-400">Loading referral partners…</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-500 dark:text-slate-400">
              <Users className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium">{search ? 'No partners match your search' : 'No referral partners yet'}</p>
              <p className="text-sm mt-1">Add a client with lead source "Referral" and a partner name to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">#</TableHead>
                    <TableHead>
                      <button onClick={() => handleSort('name')} className="flex items-center text-xs font-medium hover:text-slate-900 dark:hover:text-white">
                        Partner <SortIcon field="name" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button onClick={() => handleSort('unique_clients')} className="flex items-center text-xs font-medium hover:text-slate-900 dark:hover:text-white">
                        Clients <SortIcon field="unique_clients" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button onClick={() => handleSort('total_cases')} className="flex items-center text-xs font-medium hover:text-slate-900 dark:hover:text-white">
                        Cases <SortIcon field="total_cases" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button onClick={() => handleSort('completed_cases')} className="flex items-center text-xs font-medium hover:text-slate-900 dark:hover:text-white">
                        Completed <SortIcon field="completed_cases" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button onClick={() => handleSort('conversion_rate')} className="flex items-center text-xs font-medium hover:text-slate-900 dark:hover:text-white">
                        Conversion <SortIcon field="conversion_rate" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button onClick={() => handleSort('total_commission')} className="flex items-center text-xs font-medium hover:text-slate-900 dark:hover:text-white">
                        Commission Earned <SortIcon field="total_commission" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button onClick={() => handleSort('pending_commission')} className="flex items-center text-xs font-medium hover:text-slate-900 dark:hover:text-white">
                        Pending <SortIcon field="pending_commission" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button onClick={() => handleSort('total_loan_value')} className="flex items-center text-xs font-medium hover:text-slate-900 dark:hover:text-white">
                        Loan Value <SortIcon field="total_loan_value" />
                      </button>
                    </TableHead>
                    <TableHead>Last Case</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((p, idx) => (
                    <TableRow
                      key={p.name}
                      className="hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer"
                      onClick={() => handleSelectPartner(p)}
                    >
                      <TableCell className="text-slate-400 text-xs font-mono">
                        {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : idx + 1}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center text-xs font-bold text-red-700 dark:text-red-300 flex-shrink-0">
                            {p.name.charAt(0).toUpperCase()}
                          </div>
                          <span className="font-medium text-slate-900 dark:text-white">{p.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1 text-sm">
                          <Users className="h-3 w-3 text-slate-400" /> {p.unique_clients}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <span className="font-medium">{p.total_cases}</span>
                          {p.active_cases > 0 && <span className="text-xs text-blue-600 dark:text-blue-400 ml-1">({p.active_cases} active)</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1 text-sm text-green-700 dark:text-green-400">
                          <CheckCircle className="h-3 w-3" /> {p.completed_cases}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-slate-200 dark:bg-slate-700 rounded-full h-1.5 w-16">
                            <div
                              className="h-1.5 rounded-full bg-red-600"
                              style={{ width: `${Math.min(p.conversion_rate, 100)}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium">{p.conversion_rate}%</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-semibold text-green-700 dark:text-green-400">{formatCurrency(p.total_commission)}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-yellow-700 dark:text-yellow-400">{formatCurrency(p.pending_commission)}</span>
                      </TableCell>
                      <TableCell>{formatCurrency(p.total_loan_value)}</TableCell>
                      <TableCell className="text-xs text-slate-500">
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {formatDate(p.latest_case_date)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <ChevronRight className="h-4 w-4 text-slate-400" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
